import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Paper,
  Divider,
  Chip,
  Stack,
  MenuItem,
  Tooltip,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import { styled } from "@mui/system";
import { alpha } from "@mui/material/styles";
import { useTheme } from "styled-components";
import {
  withApiContext,
  withLabelsContext,
  withUserContext,
  withWindowContext,
} from "context";
import RubricsPreview from "./RubricsPreview";

const QuestionCard = styled(Paper)(({ theme }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  const background = "#ffffff";
  return {
    padding: "16px",
    borderRadius: 16,
    boxShadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
    border: `1px solid ${alpha(primary, 0.08)}`,
    background,
  };
});

const CriteriaPill = styled(Box)(({ theme }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  return {
    padding: "12px",
    borderRadius: 12,
    position: "relative",
    background: alpha(primary, 0.12),
    border: `1px solid ${alpha(primary, 0.2)}`,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };
});

const MarksTag = styled(Box)(({ theme }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  const background = "#ffffff";
  return {
    position: "absolute",
    bottom: "8px",
    right: "8px",
    fontSize: 11,
    fontWeight: 600,
    color: primary,
    backgroundColor: background,
    padding: "2px 8px",
    borderRadius: 999,
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  };
});

const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  padding: "24px",
  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
}));

const RubricsAssessment = (props) => {
  const { ctx_api, ctx_user } = props;
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const [tab, setTab] = useState(0);
  const [uploadFile, setUploadFile] = useState(false);
  const [activeRubricTabs, setActiveRubricTabs] = useState({});
  const [criteriaList, setCriteriaList] = useState([]);
  const [rubricTypes, setRubricTypes] = useState([]);
  const [rubricLoading, setRubricLoading] = useState(false);
  const [selectedRubricId, setSelectedRubricId] = useState("");
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [categoryInstructions, setCategoryInstructions] = useState({});
  const [selectedCriteriaIndex, setSelectedCriteriaIndex] = useState(-1);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [assessmentId, setAssessmentId] = useState(
    () =>
      location.state?.assessmentId ||
      location.state?.assessment_id ||
      null
  );
  const [assessmentName, setAssessmentName] = useState(
    () => location.state?.assessmentName || ""
  );
  const [persistingAssessment, setPersistingAssessment] = useState(false);
  const rubricResetGuardRef = useRef(false);
  const assessmentDetailsLoadedRef = useRef(false);
  const rubricsTypesLoadedRef = useRef(false);
  const lastAssessmentIdRef = useRef(null);
  const lastOrgIdRef = useRef(null);
  const lastGoiIdRef = useRef(null);

  const handleTabChange = (_e, newValue) => setTab(newValue);
  const handleUploadToggle = (_event, newVal) => {
    if (newVal !== null) setUploadFile(newVal === "yes");
  };

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const incoming =
      location.state?.assessmentId ||
      location.state?.assessment_id ||
      null;
    if (incoming && incoming !== assessmentId) {
      setAssessmentId(incoming);
    }
    if (
      location.state?.assessmentName &&
      location.state.assessmentName !== assessmentName
    ) {
      setAssessmentName(location.state.assessmentName);
    }
  }, [location.state, assessmentId, assessmentName]);

  useEffect(() => {
    if (!assessmentId || !ctx_api?.privateRequest) {
      return;
    }

    // Only call API if assessmentId has changed or hasn't been loaded yet
    if (assessmentDetailsLoadedRef.current && lastAssessmentIdRef.current === assessmentId) {
      return;
    }

    lastAssessmentIdRef.current = assessmentId;
    assessmentDetailsLoadedRef.current = true;

    let isMounted = true;

    ctx_api
      .privateRequest({
        cmd: "get_rubrics_assessment_details",
        args: {
          assessment_id: assessmentId,
        },
      })
      .then(({ rsp }) => {
        if (!isMounted) {
          return;
        }

        const normalized = Array.isArray(rsp) ? rsp[0] : rsp || {};
        if (normalized?.name) {
          setAssessmentName(normalized.name);
        }
        const incomingQuestions =
          normalized.questions ||
          normalized.assessment_questions ||
          [];

        if (Array.isArray(incomingQuestions)) {
          setQuestions(
            incomingQuestions.map((item, idx) => ({
              id: item.question_id ?? item.questionId ?? idx + 1,
              text: item.question_text ?? item.questionText ?? "",
              totalMarks: item.total_marks ?? item.totalMarks ?? 0,
              rubricType: item.rubric_type ?? item.rubricType ?? "",
              rubricId: item.rubric_id ?? item.rubricId ?? "",
              rubricData: Array.isArray(item.rubric_data ?? item.rubricData)
                ? item.rubric_data ?? item.rubricData
                : [],
              criteriaDetails: Array.isArray(
                item.criteria_details ?? item.criteriaDetails
              )
                ? item.criteria_details ?? item.criteriaDetails
                : [],
              expanded: false,
            }))
          );
        }
      })
      .catch((error) => {
        console.error("Failed to load assessment details", error);
        assessmentDetailsLoadedRef.current = false;
      });

    return () => {
      isMounted = false;
    };
  }, [assessmentId, ctx_api]);

  useEffect(() => {
    const orgId = ctx_user?.user?.organizationId;
    const goiId = ctx_user?.user?.goiId;

    if (!ctx_api?.privateRequest || !orgId || !goiId) {
      return;
    }

    // Only call API if orgId or goiId have changed or haven't been loaded yet
    if (
      rubricsTypesLoadedRef.current &&
      lastOrgIdRef.current === orgId &&
      lastGoiIdRef.current === goiId
    ) {
      return;
    }

    lastOrgIdRef.current = orgId;
    lastGoiIdRef.current = goiId;
    rubricsTypesLoadedRef.current = true;

    let active = true;
    setRubricLoading(true);

    ctx_api
      .privateRequest({
        cmd: "get_all_rubrics_types_list",
        args: {
          org_id: orgId,
          goi_id: goiId,
        },
      })
      .then(({ rsp }) => {
        if (!active) {
          return;
        }

        const list = Array.isArray(rsp) ? rsp : [];
        setRubricTypes(
          list.map((item) => ({
            id: String(item.rubrics_type_id ?? item.id ?? ""),
            name: item.name || "Untitled rubric",
            curriculumId: item.curriculum_id || null,
            categories: Array.isArray(item.categories)
              ? item.categories
              : typeof item.categories === "string"
              ? (() => {
                  try {
                    return JSON.parse(item.categories);
                  } catch {
                    return [];
                  }
                })()
              : [],
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to load rubrics types", error);
        if (active) {
          setRubricTypes([]);
        }
        rubricsTypesLoadedRef.current = false;
      })
      .finally(() => {
        if (active) {
          setRubricLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [ctx_api, ctx_user?.user?.organizationId, ctx_user?.user?.goiId]);

  const selectedRubric = useMemo(
    () => rubricTypes.find((rubric) => rubric.id === selectedRubricId),
    [rubricTypes, selectedRubricId]
  );

  const [questionInput, setQuestionInput] = useState("");
  const [criteriaNameInput, setCriteriaNameInput] = useState("");
  const [criteriaMarkInput, setCriteriaMarkInput] = useState("");
  const [instructionInput, setInstructionInput] = useState("");

  const getCategoryKey = (category, idx) =>
    category?.id != null
      ? String(category.id)
      : category?.name
      ? `${category.name}-${idx}`
      : String(idx);

  useEffect(() => {
    if (rubricResetGuardRef.current) {
      rubricResetGuardRef.current = false;
      return;
    }
    setCategoryInstructions({});
    setSelectedCriteriaIndex(-1);
    setCriteriaNameInput("");
    setCriteriaMarkInput("");
    if (selectedRubric?.categories?.length) {
      const firstKey = getCategoryKey(selectedRubric.categories[0], 0);
      setSelectedCategoryKey(firstKey);
      setInstructionInput("");
    } else {
      setSelectedCategoryKey("");
      setInstructionInput("");
    }
  }, [selectedRubric]);

  const handleCategorySelect = (key) => {
    const normalizedKey = key ?? "";
    setSelectedCategoryKey(normalizedKey);
    setInstructionInput(categoryInstructions[normalizedKey] ?? "");
  };

  const handleInstructionChange = (value) => {
    setInstructionInput(value);
    const key = selectedCategoryKey ?? "";
    setCategoryInstructions((prev) => ({
        ...prev,
      [key]: value,
    }));
  };

  const getCategoryMeta = (key) => {
    if (!selectedRubric?.categories) {
      return null;
    }
    return selectedRubric.categories.find(
      (category, idx) => getCategoryKey(category, idx) === key
    );
  };

  const getCategoryLabel = (key) => {
    if (!key) {
      return "General";
    }
    const meta = getCategoryMeta(key);
    return meta?.name || "General";
  };

  const getCategoryPercentage = (key) => {
    const meta = getCategoryMeta(key);
    if (!meta) {
      return "";
    }
    const raw =
      meta.percentage ??
      meta.percent ??
      meta.weight ??
      meta.value ??
      meta.percentageValue ??
      "";
    if (raw === null || raw === undefined) {
      return "";
    }
    return typeof raw === "number" ? `${raw}` : String(raw);
  };

  const buildQuestionsPayload = () =>
    questions.map((question, idx) => ({
      question_order: idx + 1,
      question_id: idx + 1,
      question_text: question.text,
      total_marks: question.totalMarks,
      rubric_type: question.rubricType,
      rubric_id: question.rubricId,
      rubric_data: question.rubricData,
      criteria_details: question.criteriaDetails || [],
    }));

  const handlePersistAssessment = async () => {
    if (!assessmentId) {
      window.alert("Assessment ID missing. Please reopen from Rubrics list.");
      return;
    }

    if (!questions.length) {
      window.alert("Please add at least one question before saving.");
      return;
    }

    if (!ctx_api?.privateRequest) {
      window.alert("API client unavailable. Please refresh and try again.");
      return;
    }

    const payload = {
      assessment_id: assessmentId,
      questions: buildQuestionsPayload(),
    };

    setPersistingAssessment(true);
    try {
      const { rsp } = await ctx_api.privateRequest({
        cmd: "insert_update_rubrics_assessment",
        args: payload,
      });

      const normalizedResponse = Array.isArray(rsp) ? rsp[0] : rsp || {};
      const status =
        normalizedResponse?.StatusValue ??
        normalizedResponse?.status ??
        null;

      if (status && Number(status) === 1) {
        props.ctx_window.alert("Assessment questions saved successfully.");
        history.push("/create-rubrics");
      } else {
        throw new Error(
          normalizedResponse?.StatusText ||
            "Failed to save assessment questions."
        );
      }
    } catch (error) {
      console.error("Failed to persist assessment questions", error);
      window.alert(
        error?.message || "Unable to save assessment questions right now."
      );
    } finally {
      setPersistingAssessment(false);
    }
  };

  const handleNewCriteria = () => {
    setSelectedCriteriaIndex(-1);
      setCriteriaNameInput("");
      setCriteriaMarkInput("");
    setCategoryInstructions({});
    if (selectedRubric?.categories?.length) {
      const firstKey = getCategoryKey(selectedRubric.categories[0], 0);
      setSelectedCategoryKey(firstKey);
      setInstructionInput("");
    } else {
      setSelectedCategoryKey("");
      setInstructionInput("");
    }
  };

  const handleSelectCriteria = (idx) => {
    const criterion = criteriaList[idx];
    if (!criterion) {
      return;
    }

    setSelectedCriteriaIndex(idx);
    setCriteriaNameInput(criterion.name ?? "");
    setCriteriaMarkInput(
      criterion?.mark !== undefined && criterion?.mark !== null ? String(criterion.mark) : ""
    );

    let instructionsMap =
      criterion.instructionsByCategory && Object.keys(criterion.instructionsByCategory).length > 0
        ? { ...criterion.instructionsByCategory }
        : criterion.categoryKey
        ? { [criterion.categoryKey]: criterion.instruction ?? "" }
        : {};
    if (Object.keys(instructionsMap).length === 0) {
      instructionsMap = { "": criterion.instruction ?? "" };
    }

    setCategoryInstructions(instructionsMap);

    let keyToUse = criterion.categoryKey || "";
    if (!keyToUse) {
      const mapKeys = Object.keys(instructionsMap);
      if (mapKeys.length > 0) {
        keyToUse = mapKeys[0];
      } else if (selectedRubric?.categories?.length) {
        keyToUse = getCategoryKey(selectedRubric.categories[0], 0);
      }
    }

    setSelectedCategoryKey(keyToUse);
    const instructionValue =
      keyToUse && instructionsMap[keyToUse] != null
        ? instructionsMap[keyToUse]
        : criterion.instruction ?? "";
    setInstructionInput(instructionValue);
  };

  const handleDeleteCriteria = (idx) => {
    const wasSelected = idx === selectedCriteriaIndex;
    setCriteriaList((prev) => prev.filter((_, criterionIdx) => criterionIdx !== idx));
    setSelectedCriteriaIndex((prevIdx) => {
      if (prevIdx === idx) {
        return -1;
      }
      if (prevIdx > idx) {
        return prevIdx - 1;
      }
      return prevIdx;
    });
    if (wasSelected) {
      handleNewCriteria();
    }
  };

  const handleEditQuestion = (question) => {
    if (!question) {
      return;
    }

    const nextRubricId = question.rubricId || "";
    if (selectedRubricId !== nextRubricId) {
      rubricResetGuardRef.current = true;
    }
    setEditingQuestionId(question.id);
    setQuestionInput(question.text);
    setSelectedRubricId(nextRubricId);

    const restoredCriteria = Array.isArray(question.criteriaDetails)
      ? question.criteriaDetails.map((criterion) => ({
          ...criterion,
          instructionsByCategory: { ...(criterion.instructionsByCategory || {}) },
        }))
      : [];

    setCriteriaList(restoredCriteria);

    if (restoredCriteria.length > 0) {
      const first = restoredCriteria[0];
      const instructionsMap = { ...(first.instructionsByCategory || {}) };
      const defaultKey = first.categoryKey || Object.keys(instructionsMap)[0] || "";

      setSelectedCriteriaIndex(0);
      setCriteriaNameInput(first.name ?? "");
      setCriteriaMarkInput(
        first?.mark !== undefined && first?.mark !== null ? String(first.mark) : ""
      );
      setCategoryInstructions(instructionsMap);
      setSelectedCategoryKey(defaultKey);
      setInstructionInput(
        (defaultKey && instructionsMap[defaultKey] !== undefined
          ? instructionsMap[defaultKey]
          : first.instruction) || ""
      );
    } else {
      handleNewCriteria();
    }
  };

  const addCriteria = () => {
    if (!criteriaNameInput.trim() || !criteriaMarkInput.trim()) {
      return;
    }

    const key = selectedCategoryKey ?? "";
    const instructionsMap = {
      ...categoryInstructions,
      [key]: instructionInput,
    };

    const payload = {
      name: criteriaNameInput.trim(),
      mark: Number(criteriaMarkInput),
      instruction: instructionsMap[key] ?? "",
      categoryKey: key || null,
      categoryLabel: getCategoryLabel(key),
      instructionsByCategory: instructionsMap,
    };

    if (selectedCriteriaIndex >= 0) {
      setCriteriaList((prev) =>
        prev.map((item, idx) => (idx === selectedCriteriaIndex ? { ...item, ...payload } : item))
      );
      handleNewCriteria();
    } else {
      setCriteriaList((prev) => [...prev, payload]);
      handleNewCriteria();
    }
  };

  const saveQuestion = () => {
    if (!questionInput.trim()) {
      window.alert("Please enter a question.");
      return;
    }

    const rubricData = criteriaList.map((criterion) => {
      const instructions = criterion.instructionsByCategory || {};
      const allCategoryKeys = new Set(Object.keys(instructions));

      if (selectedRubric?.categories?.length) {
        selectedRubric.categories.forEach((category, idx) => {
          allCategoryKeys.add(getCategoryKey(category, idx));
        });
      }

      if (allCategoryKeys.size === 0) {
        allCategoryKeys.add(criterion.categoryKey || "");
      }

      const categories = Array.from(allCategoryKeys).map((key) => ({
        category: getCategoryLabel(key),
        instruction: instructions[key ?? ""] || "",
        percentage: getCategoryPercentage(key),
      }));

      return {
        criteria: criterion.name,
        categories,
      };
    });

    const questionId = editingQuestionId || Date.now();
    const criteriaDetails = criteriaList.map((criterion) => ({
      ...criterion,
      instructionsByCategory: { ...(criterion.instructionsByCategory || {}) },
    }));

    const questionPayload = {
      id: questionId,
      text: questionInput.trim(),
      totalMarks: criteriaList.reduce((acc, c) => acc + c.mark, 0) || 0,
      expanded: true,
      rubricType: selectedRubric?.name || "Custom rubric",
      rubricId: selectedRubricId || null,
      rubricData,
      criteriaDetails,
    };

    if (editingQuestionId) {
      setQuestions((prev) =>
        prev.map((question) => (question.id === editingQuestionId ? questionPayload : question))
      );
    } else {
      setQuestions((prev) => [questionPayload, ...prev]);
      setActiveRubricTabs((prev) => ({
        ...prev,
        [questionId]: 0,
      }));
    }

    setQuestionInput("");
    setCriteriaList([]);
    handleNewCriteria();
    setEditingQuestionId(null);
  };

  const toggleExpand = (id) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === id ? { ...q, expanded: !q.expanded } : q
      )
    );
    setActiveRubricTabs((prev) => ({
      ...prev,
      [id]: prev[id] ?? 0,
    }));
  };

  const deleteQuestion = (id) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
    setActiveRubricTabs((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const primaryColor = theme?.color4 || theme?.primary || "#20A6D2";
  const accent = primaryColor;
  const accentLight = alpha(primaryColor, 0.12);
  const accentBorder = alpha(primaryColor, 0.24);
  const neutralBg = theme?.bodyBg || "#f2f2f2";
  const isEditingCriteria = selectedCriteriaIndex >= 0;
  const isEditingQuestion = Boolean(editingQuestionId);
  const canPersistAssessment =
    Boolean(assessmentId) && questions.length > 0 && !persistingAssessment;

  const handleAssessmentCancel = () => {
    const confirmCancel = (allow) => {
      if (allow) {
        history.push("/create-rubrics");
      }
    };
    props.ctx_window.simpleConfirm(
      "Do you want to cancel? Unsaved changes will be lost.",
      confirmCancel
    );
  };
  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        bgcolor: neutralBg,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: accentLight,
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AutoAwesomeMosaicIcon />
          </Box>
          <Box>
            
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {assessmentName || "Rubrics Assessment"}
            </Typography>
            
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "none", px: 3, borderRadius: 2 }}
            onClick={handlePersistAssessment}
            disabled={!canPersistAssessment}
          >
            {persistingAssessment ? "Saving..." : "Update"}
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", px: 3, borderRadius: 2 }}
            onClick={handleAssessmentCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 16,
          px: 2,
          py: 1,
          border: `1px solid ${alpha(accent, 0.08)}`,
          background: "#ffffff",
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              color: alpha("#111827", 0.6),
            },
            "& .Mui-selected": {
              color: accent,
            },
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: accent,
              height: 3,
              borderRadius: 3,
            },
          }}
        >
          <Tab label="Questionnaire" />
          <Tab label="Preview" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Grid
          container
          spacing={3}
          alignItems="stretch"
          sx={{ 
            width: "100%", 
            margin: 0,
            flexWrap: { xs: "wrap", md: "nowrap" }
          }}
        >
          <Grid
            item
            sx={{
              flexBasis: { xs: "100%", md: "40%" },
              maxWidth: { xs: "100%", md: "40%" },
            }}
          >
            <SectionCard>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  Create Question
                </Typography>
                {/* <Button
                  startIcon={<LibraryBooksIcon />}
                  size="small"
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Add from library
                </Button> */}
              </Box>

              <TextField
                placeholder="Briefly explain about the soil?"
                fullWidth
                size="small"
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />

              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  Do you need to upload file?
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={uploadFile ? "yes" : "no"}
                  exclusive
                  onChange={handleUploadToggle}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      minWidth: 64,
                      borderRadius: 2,
                      borderColor: accentBorder,
                    },
                    "& .Mui-selected": {
                      backgroundColor: accentLight,
                      color: accent,
                      borderColor: accentBorder,
                    },
                  }}
                >
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  Rubric
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedRubricId}
                  onChange={(e) => setSelectedRubricId(e.target.value)}
                  disabled={criteriaList.length > 0}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: alpha("#111827", 0.02),
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    {rubricLoading ? "Loading rubrics..." : "Select Rubric"}
                  </MenuItem>
                  {rubricTypes.map((rubric) => (
                    <MenuItem key={rubric.id} value={rubric.id}>
                      {rubric.name}
                    </MenuItem>
                  ))}
                </TextField>
                
              </Box>

              {criteriaList.length > 0 && (
                <Stack spacing={0.75} mt={1}>
                    <Typography variant="caption" color="text.secondary">
                    Existing Criteria
                    </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      overflowX: "auto",
                      flexWrap: "wrap",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                      py: 0.5,
                    }}
                  >
                    {criteriaList.map((criterion, idx) => {
                      console.log(criteriaList)
                      const isActive = idx === selectedCriteriaIndex;
                      return (
                        <Box
                          key={`${criterion.name}-${idx}-ribbon`}
                          onClick={() => handleSelectCriteria(idx)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 1.5,
                            border: `1px solid ${isActive ? accent : alpha(accent, 0.2)}`,
                            backgroundColor: isActive ? alpha(accent, 0.16) : alpha(accent, 0.06),
                            color: isActive ? accent : "#111827",
                            cursor: "pointer",
                            minWidth: 0,
                            boxShadow: isActive ? `0 4px 12px ${alpha(accent, 0.15)}` : "none",
                          }}
                        >
                          <Tooltip title={criterion.name} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {criterion.name.length > 8
                                ? `${criterion.name.substring(0, 8)}…`
                                : criterion.name}
                            </Typography>
                          </Tooltip>

                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 500, whiteSpace: "nowrap", color: "inherit" }}
                          >
                            Mark: {criterion.mark}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{ color: "inherit" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectCriteria(idx);
                            }}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: "inherit" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteCriteria(idx);
                            }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      );
                    })}

                    <Button
                      variant="text"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleNewCriteria();
                      }}
                      sx={{
                        px: 1.75,
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        backgroundColor: alpha(accent, 0.04),
                        color: accent,
                      }}
                    >
                      + New
                    </Button>
                  </Stack>
                </Stack>
              )}

              <Divider />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Criteria
                </Typography>
                <Button
                  startIcon={
                    isEditingCriteria ? <EditIcon fontSize="small" /> : <AddCircleOutlineIcon />
                  }
                  size="small"
                  onClick={addCriteria}
                  disabled={
                    !criteriaNameInput.trim() ||
                    !criteriaMarkInput.trim() ||
                    (selectedRubric?.categories?.length > 0 && !selectedCategoryKey)
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 2,
                    color: accent,
                  }}
                >
                  {isEditingCriteria ? "Update Criteria" : "Add Criteria"}
                </Button>
              </Box>
              

              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Criterion Name"
                    size="small"
                    fullWidth
                    value={criteriaNameInput}
                    onChange={(e) => setCriteriaNameInput(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Mark"
                    size="small"
                    type="number"
                    value={criteriaMarkInput}
                    onChange={(e) => setCriteriaMarkInput(e.target.value)}
                    sx={{
                      width: { xs: "100%", sm: 140 },
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                    inputProps={{ min: 0 }}
                  />
                </Stack>
                {selectedRubric?.categories?.length > 0 && (
                  <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary">
                      Categories
                  </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        flexWrap: "nowrap",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                      }}
                    >
                      {selectedRubric.categories.map((category, idx) => {
                        const key = getCategoryKey(category, idx);
                        const isSelected = key === selectedCategoryKey;
                        return (
                      <Chip
                            key={`${selectedRubric.id}-category-${key}`}
                            label={
                              category?.percentage
                                ? `${category?.name ?? "Unnamed"} • ${category.percentage}`
                                : category?.name || "Unnamed"
                            }
                            clickable
                            onClick={() => handleCategorySelect(key)}
                        sx={{
                          borderRadius: 10,
                              backgroundColor: isSelected ? accent : accentLight,
                              color: isSelected ? "#ffffff" : accent,
                          fontWeight: 500,
                              maxWidth: 160,
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                        }}
                      />
                        );
                      })}
                  </Stack>
                </Stack>
              )}

                <TextField
                  multiline
                  rows={3}
                  placeholder="Proficient Instruction"
                  fullWidth
                  value={instructionInput}
                  onChange={(e) => handleInstructionChange(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={saveQuestion}
                  disabled={criteriaList.length === 0}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  {isEditingQuestion ? "Update Question" : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  sx={{ textTransform: "none", borderRadius: 2 }}
                  onClick={() => {
                    setQuestionInput("");
                    handleNewCriteria();
                    setCriteriaList([]);
                    setEditingQuestionId(null);
                    setSelectedRubricId("");
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid
            item
            sx={{
              flexBasis: { xs: "100%", md: "60%" },
              maxWidth: { xs: "100%", md: "60%" },
            }}
          >
            <SectionCard sx={{ gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Questions
              </Typography>

              {questions.length === 0 ? (
                <QuestionCard sx={{ py: 6, alignItems: "center", textAlign: "center" }}>
                  <Stack spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: alpha(accent, 0.1),
                        color: accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AutoAwesomeMosaicIcon />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      No questions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" maxWidth={280}>
                      Create a question.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      sx={{ textTransform: "none", borderRadius: 2 }}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                      Add a question
                    </Button>
                  </Stack>
                </QuestionCard>
              ) : (
                questions.map((question, qIdx) => {
                  const activeTab = activeRubricTabs[question.id] ?? 0;
                  return (
                <QuestionCard key={question.id} sx={{ mb: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Q{qIdx + 1}. {question.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Marks: {question.totalMarks}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              sx={{ bgcolor: accentLight, color: accent }}
                              onClick={() => handleEditQuestion(question)}
                            >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: alpha("#ef4444", 0.08), color: "#ef4444" }}
                          onClick={() => deleteQuestion(question.id)}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: alpha(accent, 0.08), color: accent }}
                          onClick={() => toggleExpand(question.id)}
                        >
                          {question.expanded ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Collapse in={question.expanded} timeout="auto" unmountOnExit>
                      <Tabs
                            value={activeTab}
                            onChange={(e, val) =>
                              setActiveRubricTabs((prev) => ({
                                ...prev,
                                [question.id]: val,
                              }))
                            }
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          borderBottom: `1px solid ${alpha(accent, 0.1)}`,
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            color: alpha("#111827", 0.6),
                          },
                          "& .Mui-selected": {
                            color: accent,
                          },
                        }}
                        TabIndicatorProps={{
                          style: {
                            backgroundColor: accent,
                            height: 3,
                            borderRadius: 3,
                          },
                        }}
                      >
                            {question.rubricData?.map((item, idx) => (
                              <Tab key={`${question.id}-criteria-${idx}`} label={item.criteria} />
                            )) || null}
                      </Tabs>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                            {question.rubricData?.map((criteriaEntry, cIdx) =>
                              activeTab === cIdx
                                ? criteriaEntry.categories.map((item, i) => (
                                    <Grid item xs={12} sm={6} key={`${criteriaEntry.criteria}-${i}`}>
                                  <CriteriaPill>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        textAlign: "left",
                                        fontWeight: 700,
                                        color: accent,
                                      }}
                                    >
                                          {item.category}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                          {item.instruction || "No instruction provided"}
                                    </Typography>
                                        {item.percentage ? <MarksTag>{item.percentage}</MarksTag> : null}
                                  </CriteriaPill>
                                </Grid>
                              ))
                            : null
                        )}
                      </Grid>
                    </Collapse>
                  </Stack>
                </QuestionCard>
                  );
                })
              )}
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <RubricsPreview questions={questions} assessmentName={assessmentName} />
      )}
    </Box>
  );
};


export default withApiContext(
  withUserContext(withWindowContext(withLabelsContext(RubricsAssessment)))
);
